import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import { msalInstance } from './authConfig';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import ErrorBoundary from './components/ErrorBoundary';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import Assets from './pages/Assets';
import AssetDetail from './pages/AssetDetail';
import Employees from './pages/Employees';
import EmployeeDetail from './pages/EmployeeDetail';
import Locations from './pages/Locations';
import DamageReports from './pages/DamageReports';
import Reports from './pages/Reports';
import Integrations from './pages/Integrations';
import AuditLog from './pages/AuditLog';
import MapPage from './pages/Map';
import { canAccessPath, FALLBACK_PATH } from './access';
import './index.css';

const CENTERED = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  height: '100vh', flexDirection: 'column', gap: 12, padding: 24,
};

function FullScreenSpinner({ label }) {
  return (
    <div style={CENTERED}>
      <div className="spinner" style={{ width: 32, height: 32 }} />
      {label && <p style={{ color: 'var(--text-2)', fontSize: 14 }}>{label}</p>}
    </div>
  );
}

// Shown when Microsoft accepted the sign-in but the API would not hand back a
// profile. The API writes these messages for end users — a missing INV group
// membership comes back as "Contact IT to request access" — so it is repeated
// verbatim rather than replaced with something generic.
//
// Sign out matters as much as Retry here: this state used to be unescapable
// without editing the URL, because Layout (and with it the only sign-out
// button) never mounts.
function ProfileError({ error, onRetry, onSignOut }) {
  return (
    <div style={CENTERED}>
      <div style={{ fontSize: 32 }}>⚠</div>
      <h1 style={{ fontSize: 16, fontWeight: 600 }}>Can’t open your account</h1>
      <p style={{ color: 'var(--text-2)', fontSize: 14, maxWidth: 420, textAlign: 'center', lineHeight: 1.6 }}>
        {error?.message || 'The server did not return your profile.'}
      </p>
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button className="btn btn-primary" onClick={onRetry}>Try again</button>
        <button className="btn" onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}

function RequireAuth({ children }) {
  const { user, loading, error, isAuthenticated, retry, logout } = useAuth();
  if (loading) return <FullScreenSpinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  // Past this point loading is done, so "no user" is a failure rather than a
  // wait — with or without an error object to explain it.
  if (!user) return <ProfileError error={error} onRetry={retry} onSignOut={logout} />;
  return children;
}

// Hiding a nav link isn't access control — guard the route too, so a typed or
// bookmarked URL can't reach a page the sidebar withholds.
//
// This replaces a guard that covered Integrations alone. Every other restricted
// page was hidden and nothing more: the Audit Log never appeared for HR or
// read-only, and /audit answered them anyway. One wrapper around Layout now
// checks whatever path is showing, against the same table the sidebar filters
// with, so a page can't be added to one and forgotten in the other.
function RequireAccess({ children }) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (!canAccessPath(user?.role, pathname)) {
    return <Navigate to={FALLBACK_PATH} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/" element={
        <RequireAuth>
          <RequireAccess>
            <Layout />
          </RequireAccess>
        </RequireAuth>
      }>
        <Route index element={<Navigate to={FALLBACK_PATH} replace />} />
        <Route path="dashboard"  element={<Dashboard />} />
        <Route path="assets"     element={<Assets />} />
        <Route path="assets/:id" element={<AssetDetail />} />
        <Route path="employees"  element={<Employees />} />
        <Route path="employees/:id" element={<EmployeeDetail />} />
        <Route path="locations"  element={<Locations />} />
        <Route path="damage"     element={<DamageReports />} />
        <Route path="reports"    element={<Reports />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="audit"      element={<AuditLog />} />
        <Route path="map"        element={<MapPage />} />
      </Route>
      <Route path="*" element={<Navigate to={FALLBACK_PATH} replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthProvider>
        <BrowserRouter>
          {/* Layout has its own boundary around the page area. This outer one
              only catches what that can't — a throw in the shell itself, or in
              the login and callback screens, which render outside Layout. */}
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </BrowserRouter>
      </AuthProvider>
    </MsalProvider>
  );
}
